/**
 * BoboDataLayer — Centralized GTM DataLayer Manager
 *
 * Provides a unified API to push events to the GTM data layer,
 * automatically injecting storeContext and userContext on every push.
 *
 * Initialization:
 *   window.BoboDataLayer.init({ datalayerName, storeContext, userContext });
 *
 * Usage:
 *   window.BoboDataLayer.push('page_view', { genericUrl: '/shoes', ... });
 *   window.BoboDataLayer.pushEcommerce('add_to_cart', { currency: 'EUR', items: [...] });
 */
(function () {
  'use strict';

  /** @type {string} */
  let _datalayerName = 'dataLayer';

  /** @type {Object} */
  let _storeContext = {};

  /** @type {Object} */
  let _userContext = {};

  /** @type {boolean} */
  let _initialized = false;

  /** @type {boolean} */
  let _debug = false;

  /** @type {number} Max age in hours for list context entries */
  let _listContextMaxAgeHours = 24;

  /** @type {number} Max number of list context entries */
  let _listContextMaxItems = 100;

  /**
   * Returns the target data layer array from window.
   * @returns {Array}
   */
  function getDataLayer() {
    window[_datalayerName] = window[_datalayerName] || [];
    return window[_datalayerName];
  }

  /**
   * Reads, purges old and over-limit entries, and returns the current list context map from localStorage.
   * @returns {Object}
   */
  function getAndPurgeListContexts() {
    var maxAge = _listContextMaxAgeHours * 60 * 60 * 1000;
    var maxItems = _listContextMaxItems;
    var contexts = {};
    
    try {
      var data = localStorage.getItem('bobo_gtm_lists_context');
      if (data) {
        contexts = JSON.parse(data);
      }
    } catch(e) {
      if (_debug) console.error('[GTM Debug] Error parsing lists context from localStorage', e);
      return {};
    }

    var now = Date.now();
    var keys = Object.keys(contexts);
    var needsUpdate = false;
    var validEntries = [];

    // Filter out expired items
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if ((now - contexts[key].timestamp) > maxAge) {
        if (_debug) console.log('[GTM Debug] Purging expired list context for item: ' + key);
        delete contexts[key];
        needsUpdate = true;
      } else {
        validEntries.push({ key: key, timestamp: contexts[key].timestamp });
      }
    }

    // Limit to maxItems (keep the most recent ones)
    if (validEntries.length > maxItems) {
      validEntries.sort(function(a, b) { return b.timestamp - a.timestamp; }); // Descending by timestamp
      for (var j = maxItems; j < validEntries.length; j++) {
        if (_debug) console.log('[GTM Debug] Purging over-limit list context for item: ' + validEntries[j].key);
        delete contexts[validEntries[j].key];
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      try {
        localStorage.setItem('bobo_gtm_lists_context', JSON.stringify(contexts));
      } catch(e) {
        if (_debug) console.error('[GTM Debug] Error saving purged lists context', e);
      }
    }

    return contexts;
  }

  window.BoboDataLayer = {
    /**
     * Initializes the manager with context data rendered server-side via Liquid.
     * @param {Object} config
     * @param {string} config.datalayerName - 'dataLayer' or 'groPulseGTMDatalayer'
     * @param {Object} config.storeContext  - Store-level context (codeVersion, storeIso, etc.)
     * @param {Object} config.userContext   - User-level context (loginStatus, userId, etc.)
     */
    init: function (config) {
      _datalayerName = config.datalayerName || 'dataLayer';
      _storeContext = config.storeContext || {};
      _userContext = config.userContext || {};
      _debug = config.debug || false;
      _listContextMaxAgeHours = config.listContextMaxAgeHours || 24;
      _listContextMaxItems = config.listContextMaxItems || 100;
      _initialized = true;
      getDataLayer();

      if (_debug) {
        console.groupCollapsed('[BoboDataLayer] Initialized');
        console.log('Mode:', _datalayerName);
        console.log('Store Context:', _storeContext);
        console.log('User Context:', _userContext);
        console.groupEnd();
      }

      // 1. Process pending view_item_list BEFORE page_view
      try {
        var pendingGtmData = sessionStorage.getItem('bobo_gtm_pending_view_item_list');
        if (pendingGtmData) {
          var parsedData = JSON.parse(pendingGtmData);
          if (parsedData && parsedData.items && parsedData.items.length > 0) {
            this.pushEcommerce('view_item_list', {
              item_list_id: parsedData.items[0].itemListId,
              item_list_name: parsedData.items[0].itemListName,
              items: parsedData.items
            });
            if (_debug) {
              console.log('[GTM Debug] Flushed pending view_item_list from previous page (' + parsedData.items.length + ' items)');
            }
          }
          sessionStorage.removeItem('bobo_gtm_pending_view_item_list');
        }
      } catch(e) {
        if (_debug) console.error('[GTM Debug] Error parsing pending view_item_list', e);
        sessionStorage.removeItem('bobo_gtm_pending_view_item_list');
      }

      // 1.5 Process pending Klevu view_item_list BEFORE page_view
      try {
        var pendingKlevuData = sessionStorage.getItem('bobo_gtm_pending_klevu_view_item_list');
        if (pendingKlevuData) {
          var parsedKlevu = JSON.parse(pendingKlevuData);
          if (parsedKlevu && parsedKlevu.length > 0) {
            var groupedByList = {};
            parsedKlevu.forEach(function(item) {
              var lid = item.itemListId;
              if (!groupedByList[lid]) groupedByList[lid] = { name: item.itemListName, items: [] };
              groupedByList[lid].items.push(item);
            });
            Object.keys(groupedByList).forEach(function(lid) {
              this.pushEcommerce('view_item_list', {
                item_list_id: lid,
                item_list_name: groupedByList[lid].name,
                items: groupedByList[lid].items
              });
              if (_debug) {
                console.log('[GTM Debug] Flushed pending Klevu view_item_list "' + lid + '" from previous page (' + groupedByList[lid].items.length + ' items)');
              }
            }.bind(this));
          }
          sessionStorage.removeItem('bobo_gtm_pending_klevu_view_item_list');
        }
      } catch(e) {
        if (_debug) console.error('[GTM Debug] Error parsing pending klevu view_item_list', e);
        sessionStorage.removeItem('bobo_gtm_pending_klevu_view_item_list');
      }

      // Process pending select_item BEFORE page_view
      try {
        var pendingSelectData = localStorage.getItem('bobo_gtm_pending_select_item');
        if (pendingSelectData) {
          var parsedSelectData = JSON.parse(pendingSelectData);
          if (parsedSelectData && parsedSelectData.items && parsedSelectData.items.length > 0) {
            this.pushEcommerce('select_item', parsedSelectData);
            if (_debug) {
              console.log('[GTM Debug] Flushed pending select_item from previous page');
            }
          }
          localStorage.removeItem('bobo_gtm_pending_select_item');
        }
      } catch(e) {
        if (_debug) console.error('[GTM Debug] Error parsing pending select_item', e);
        localStorage.removeItem('bobo_gtm_pending_select_item');
      }

      // 2. Auto-trigger page_view if data is provided
      if (config.pageData) {
        this.push('page_view', config.pageData);
      }
    },

    /**
     * Pushes a generic event with storeContext and userContext auto-attached.
     * @param {string} eventName
     * @param {Object} [eventData={}] - Additional event payload
     */
    push: function (eventName, eventData) {
      if (!_initialized) {
        console.warn('[BoboDataLayer] Not initialized. Call init() first.');
        return;
      }

      var payload = Object.assign(
        { event: eventName },
        eventData,
        { storeContext: _storeContext, userContext: _userContext }
      );

      if (_debug) {
        console.log('[GTM Debug] Push:', eventName, payload);
      }

      getDataLayer().push(payload);
    },

    /**
     * Pushes an ecommerce event (GA4 format).
     * Clears previous ecommerce object before pushing.
     * @param {string} eventName  - e.g. 'add_to_cart', 'view_item', 'purchase'
     * @param {Object} ecommerceData - The ecommerce payload (currency, value, items, etc.)
     * @param {Object} [extraData={}] - Extra top-level fields (atcType, etc.)
     */
    pushEcommerce: function (eventName, ecommerceData, extraData) {
      if (!_initialized) {
        console.warn('[BoboDataLayer] Not initialized. Call init() first.');
        return;
      }

      var dl = getDataLayer();

      var payload = Object.assign(
        { event: eventName },
        extraData || {},
        ecommerceData,
        { storeContext: _storeContext, userContext: _userContext }
      );

      if (_debug) {
        console.groupCollapsed('[GTM Debug] Ecommerce Push:', eventName);
        console.log('Payload:', payload);
        console.groupEnd();
      }

      dl.push(payload);
    },

    /**
     * Returns a copy of the current store context.
     * @returns {Object}
     */
    getStoreContext: function () {
      return Object.assign({}, _storeContext);
    },

    /**
     * Returns a copy of the current user context.
     * @returns {Object}
     */
    getUserContext: function () {
      return Object.assign({}, _userContext);
    },

    /**
     * Returns true if debug mode is active.
     * @returns {boolean}
     */
    isDebug: function () {
      return _debug;
    },

    /**
     * Returns true if data layer was initialized.
     * @returns {boolean}
     */
    isInitialized: function () {
      return _initialized;
    },

    /**
     * Returns the datalayer name.
     * @returns {string}
     */
    getDatalayerName: function () {
      return _datalayerName;
    },

    /**
     * Reads the grid module a product card belongs to, as the card exposes it.
     *
     * One reader for every consumer of the contract: the list event, the click capture,
     * the add to cart and the context handed over to the product page all have to agree
     * on which attributes carry it and on the types they report.
     *
     * A card outside a module carries only the type, so the slot comes back undefined and
     * drops out of the payload instead of travelling as an empty string.
     *
     * @param {Element} element card element carrying the data-module-* attributes
     * @returns {{moduleType: (string|undefined), moduleSlot: (number|undefined)}}
     */
    getModuleContext: function(element) {
      if (!element || !element.dataset) return {};
      var slot = parseInt(element.dataset.moduleSlot, 10);
      return {
        moduleType: element.dataset.moduleType || undefined,
        moduleSlot: isNaN(slot) ? undefined : slot
      };
    },

    /**
     * Saves list context for a specific item to localStorage.
     * @param {string} itemId
     * @param {number|string} listId
     * @param {string} listName
     * @param {number|string} index
     * @param {Object} [moduleContext] output of getModuleContext, so the product page can
     *   report the module the visit started from
     */
    setListContext: function(itemId, listId, listName, index, moduleContext) {
      if (!itemId) return;
      var module = moduleContext || {};
      var contexts = getAndPurgeListContexts();
      contexts[itemId] = {
        itemListId: listId,
        itemListName: listName,
        index: index,
        moduleType: module.moduleType,
        moduleSlot: module.moduleSlot,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem('bobo_gtm_lists_context', JSON.stringify(contexts));
      } catch(e) {
        if (_debug) console.error('[GTM Debug] Error saving list context', e);
      }
    },

    /**
     * Retrieves the list context from localStorage for a specific item.
     * @param {string} itemId
     * @returns {Object|null}
     */
    getListContext: function(itemId) {
      if (!itemId) return null;
      var contexts = getAndPurgeListContexts();
      if (contexts[itemId]) {
        return contexts[itemId];
      }
      if (itemId.length === 8) {
        var keys = Object.keys(contexts);
        for (var i = 0; i < keys.length; i++) {
          if (keys[i].length === 9 && keys[i].substring(0, 8) === itemId) {
            return contexts[keys[i]];
          }
        }
      }
      return null;
    }
  };
})();
